/**
 * FileUploader - Upload assets to Firebase Storage
 * Handles images, videos, models, and audio files for Pro users
 * Includes storage quota enforcement
 */

import { Logger } from './Logger';
import { storage } from '../firebase/config';

const logger = Logger.getLogger('FileUploader');
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  type UserProfile,
  hasStorageAvailable,
  getStorageLimit,
  updateStorageUsage,
} from '../api/UserProfileAPI';

export type AssetType = 'image' | 'video' | 'model' | 'audio';

export interface UploadProgress {
  bytesTransferred: number;
  totalBytes: number;
  progress: number; // 0-100
}

export interface UploadResult {
  url: string;
  path: string;
  size: number;
  contentType: string;
}

// File type validation
const ALLOWED_EXTENSIONS: Record<AssetType, string[]> = {
  image: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'],
  video: ['.mp4', '.webm', '.mov', '.avi', '.mkv'],
  model: ['.glb', '.gltf'],  // Only glTF formats are supported by Babylon.js loader
  audio: ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac']
};

const MIME_TYPES: Record<AssetType, string[]> = {
  image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'],
  video: ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska'],
  model: ['model/gltf-binary', 'model/gltf+json', 'application/octet-stream'],  // Only glTF MIME types
  audio: ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4', 'audio/aac', 'audio/flac']
};

/**
 * Validate file type
 */
export function validateFileType(file: File, assetType: AssetType): boolean {
  const fileName = file.name.toLowerCase();
  const fileType = file.type.toLowerCase();

  // Check extension
  const hasValidExtension = ALLOWED_EXTENSIONS[assetType].some(ext => fileName.endsWith(ext));

  // Check MIME type
  const hasValidMime = MIME_TYPES[assetType].some(mime => fileType.includes(mime.toLowerCase()));

  return hasValidExtension || hasValidMime;
}

/**
 * Generate storage path for uploaded file
 */
export function generateStoragePath(
  userId: string,
  sequenceId: string,
  assetType: AssetType,
  fileName: string
): string {
  // Sanitize filename
  const sanitized = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
  const timestamp = Date.now();

  return `uploads/${userId}/${sequenceId}/${assetType}s/${timestamp}_${sanitized}`;
}

/**
 * Upload file to Firebase Storage
 * @param file File to upload
 * @param userId User ID
 * @param sequenceId Sequence ID
 * @param assetType Type of asset
 * @param onProgress Optional progress callback
 * @returns Upload result with URL and metadata
 */
export async function uploadFile(
  file: File,
  userId: string,
  sequenceId: string,
  assetType: AssetType,
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadResult> {
  // Validate file type
  if (!validateFileType(file, assetType)) {
    throw new Error(`Invalid file type for ${assetType}. File: ${file.name}, Type: ${file.type}`);
  }

  // Generate storage path
  const storagePath = generateStoragePath(userId, sequenceId, assetType, file.name);
  const storageRef = ref(storage, storagePath);

  // Create upload task
  const uploadTask = uploadBytesResumable(storageRef, file, {
    contentType: file.type,
    customMetadata: {
      userId,
      sequenceId,
      assetType,
      originalName: file.name,
      uploadedAt: new Date().toISOString()
    }
  });

  return new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        // Progress callback
        if (onProgress) {
          const progress: UploadProgress = {
            bytesTransferred: snapshot.bytesTransferred,
            totalBytes: snapshot.totalBytes,
            progress: Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
          };
          onProgress(progress);
        }
      },
      (error) => {
        // Error callback
        logger.error('Upload error:', error);
        reject(new Error(`Upload failed: ${error.message}`));
      },
      async () => {
        // Success callback
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

          const result: UploadResult = {
            url: downloadURL,
            path: storagePath,
            size: uploadTask.snapshot.totalBytes,
            contentType: file.type
          };

          logger.info('Upload complete:', result);
          resolve(result);
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Unknown error';
          reject(new Error(`Failed to get download URL: ${message}`));
        }
      }
    );
  });
}

/**
 * Delete uploaded file from storage
 */
export async function deleteFile(storagePath: string): Promise<void> {
  try {
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
    logger.info('File deleted:', storagePath);
  } catch (error) {
    logger.error('Error deleting file:', error);
    const message = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Failed to delete file: ${message}`);
  }
}

/**
 * Format bytes to human-readable size
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Get file extension
 */
export function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  return parts.length > 1 ? `.${parts.pop()?.toLowerCase()}` : '';
}

/**
 * Check if file size is within limits (for future quota enforcement)
 * Currently unlimited, but structure is in place
 */
export function checkFileSizeLimit(fileSize: number, maxSize?: number): boolean {
  if (!maxSize) return true; // No limit if maxSize not specified
  return fileSize <= maxSize;
}

/**
 * Upload file with storage quota enforcement
 * Checks quota before upload and updates usage after successful upload
 *
 * @param file File to upload
 * @param userId User ID
 * @param sequenceId Sequence ID
 * @param assetType Type of asset
 * @param userProfile User profile for quota checking
 * @param onProgress Optional progress callback
 * @returns Upload result with URL and metadata
 * @throws Error if quota would be exceeded
 */
export async function uploadFileWithQuotaCheck(
  file: File,
  userId: string,
  sequenceId: string,
  assetType: AssetType,
  userProfile: UserProfile,
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadResult> {
  // Check storage quota before upload
  const storageLimit = getStorageLimit(userProfile);

  // If user has no storage (free tier), block upload
  if (storageLimit === 0) {
    throw new Error('Your plan does not include cloud storage. Please upgrade to upload files.');
  }

  // Check if this upload would exceed quota (unless unlimited)
  if (storageLimit !== -1 && !hasStorageAvailable(userProfile, file.size)) {
    const currentUsed = userProfile.storageUsed ?? 0;
    const remaining = Math.max(0, storageLimit - currentUsed);
    throw new Error(
      `Storage quota exceeded. You have ${formatFileSize(remaining)} remaining, ` +
      `but this file is ${formatFileSize(file.size)}. Please upgrade your plan or delete some files.`
    );
  }

  // Perform the upload
  const result = await uploadFile(file, userId, sequenceId, assetType, onProgress);

  // Update storage usage after successful upload
  try {
    const newUsage = (userProfile.storageUsed ?? 0) + result.size;
    await updateStorageUsage(userId, newUsage);
    logger.debug(`Storage updated: ${formatFileSize(newUsage)} / ${storageLimit === -1 ? 'Unlimited' : formatFileSize(storageLimit)}`);
  } catch (error) {
    // Log but don't fail - the upload succeeded, storage tracking is secondary
    logger.warn('Failed to update storage usage:', error);
  }

  return result;
}

/**
 * Delete file and update storage usage
 * @param storagePath Path to the file in storage
 * @param fileSize Size of the file being deleted (for quota tracking)
 * @param userId User ID (for updating usage)
 * @param currentStorageUsed Current storage used (to calculate new total)
 */
export async function deleteFileWithQuotaUpdate(
  storagePath: string,
  fileSize: number,
  userId: string,
  currentStorageUsed: number
): Promise<void> {
  // Delete the file
  await deleteFile(storagePath);

  // Update storage usage
  try {
    const newUsage = Math.max(0, currentStorageUsed - fileSize);
    await updateStorageUsage(userId, newUsage);
    logger.debug(`Storage freed: ${formatFileSize(fileSize)}, new total: ${formatFileSize(newUsage)}`);
  } catch (error) {
    logger.warn('Failed to update storage usage after delete:', error);
  }
}
